import { CaretRightOutlined } from "@ant-design/icons";
import { ReactComponent as NavToFirst } from "../../assets/Icons/navToFirst.svg";
import { ReactComponent as NavToLast } from "../../assets/Icons/navToLast.svg";
import { ReactComponent as PrevIcon } from "../../assets/Icons/prevIcon.svg";
import { ReactComponent as NextIcon } from "../../assets/Icons/nextIcon.svg";
import { Space, Table, Pagination, Select, Button, ConfigProvider } from "antd";
import React, { useState, useEffect, useRef, memo } from "react";
import { ReactComponent as DownArrow } from "../../assets/Icons/DownArrow.svg";
import { ReactComponent as UpArrow } from "../../assets/Icons/UpArrow.svg";
import "./ReusableTable.scss";

/* Must match the exit animation in ReusableTable.scss. */
const ROW_EXIT_MS = 420;

const ReusableTable = ({
  columns,
  dataSource = [],
  rowKey = "key",
  loading = false,
  bordered = false,
  expandedRowRender = null,
  defaultExpandedRowKeys = [],
  pageSize = 10,
  setPageSize,
  CurrentPageNo = 1,
  triggerChange,
  totalItems = 0,
  rowSelection = null,
  pointer = false,
  className = "",
  rowClass = "",
  onSortChange = null,

  onRowClick = () => {},
}) => {
  const { Option } = Select;

  const [pagination, setPagination] = useState({
    current: CurrentPageNo,
    pageSize: pageSize || 10,
    total: totalItems,
  });
  const [tableData, setTableData] = useState([]);
  const totalPages = Math.ceil(pagination.total / pagination.pageSize);
  const tableRef = useRef(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState(
    defaultExpandedRowKeys,
  );
  const [closingRowKeys, setClosingRowKeys] = useState([]);
  const closeTimers = useRef({});

  // pending exits must not fire after the table goes away
  useEffect(
    () => () => {
      Object.values(closeTimers.current).forEach(clearTimeout);
      closeTimers.current = {};
    },
    [],
  );

  useEffect(() => {
    setPagination({
      current: CurrentPageNo,
      pageSize: pageSize || 10,
      total: totalItems,
    });
  }, [CurrentPageNo, pageSize, totalItems]);

  useEffect(() => {
    setExpandedRowKeys(defaultExpandedRowKeys);
  }, []);

  /* rowKey can be a string or a resolver function, so read it through here
     rather than indexing the record directly. */
  const getRowKey = (record) =>
    typeof rowKey === "function" ? rowKey(record) : record?.[rowKey];

  /* antd drops an expanded row from the DOM the moment its key leaves
     expandedRowKeys, so a closing panel had nothing left to animate. A key
     being closed is held in expandedRowKeys for the length of the exit
     animation and flagged here, which keeps the row mounted while it plays. */
  const finishClose = (key) => {
    clearTimeout(closeTimers.current[key]);
    delete closeTimers.current[key];
    setExpandedRowKeys((prev) => prev.filter((k) => k !== key));
    setClosingRowKeys((prev) => prev.filter((k) => k !== key));
  };

  const beginClose = (key) => {
    setClosingRowKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));

    /* The row is normally unmounted by the wrapper's animationend, which
       lands on the exact frame the animation finishes - a fixed timer drifts
       a frame or two past it and the panel sat at zero height in between,
       which is the hitch at the end of the close. This is only a fallback for
       when no animation runs at all (reduced motion), so it is given a
       little slack. */
    clearTimeout(closeTimers.current[key]);
    closeTimers.current[key] = setTimeout(
      () => finishClose(key),
      ROW_EXIT_MS + 120,
    );
  };

  /* One row open at a time: opening another animates the previous one shut
     instead of removing it in the same commit, which is what made the table
     jump between the two. */
  const handleExpand = (expanded, record) => {
    const key = getRowKey(record);

    if (!expanded) {
      beginClose(key);
      return;
    }

    expandedRowKeys.forEach((openKey) => {
      if (openKey !== key) beginClose(openKey);
    });

    // reopening a row mid-close cancels its exit
    clearTimeout(closeTimers.current[key]);
    delete closeTimers.current[key];
    setClosingRowKeys((prev) => prev.filter((k) => k !== key));
    setExpandedRowKeys((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
  };
  const expandable = expandedRowRender
    ? {
        /* The grid wrapper animates to the content's real height, so no
           max-height guess is needed and open/close are symmetrical. */
        expandedRowRender: (record, ...rest) => {
          const key = getRowKey(record);
          const isClosing = closingRowKeys.includes(key);

          return (
            <div
              className={`reusable-expanded ${isClosing ? "is-closing" : ""}`}
              onAnimationEnd={(event) => {
                /* the panel's own staggered children bubble animationend too */
                if (isClosing && event.target === event.currentTarget) {
                  finishClose(key);
                }
              }}
            >
              <div className="reusable-expanded__inner">
                {expandedRowRender(record, ...rest)}
              </div>
            </div>
          );
        },
        expandedRowKeys,
        onExpand: handleExpand,
        expandIcon: ({ expanded, onExpand, record }) => (
          <span
            onClick={(e) => onExpand(record, e)}
            className={`custom-expand-icon ${expanded ? "expanded" : ""}`}
          >
            {expanded ? (
              <UpArrow style={{ cursor: "pointer" }} />
            ) : (
              <DownArrow style={{ cursor: "pointer" }} />
            )}
          </span>
        ),
        rowExpandable: () => true,
      }
    : undefined;

  useEffect(() => {
    const container = tableRef.current;
    const tableBody = container?.querySelector(".ant-table-content");

    if (!tableBody || !container) return;

    const handleScroll = () => {
      const scrollLeft = tableBody.scrollLeft;
      const scrollWidth = tableBody.scrollWidth;
      const clientWidth = tableBody.clientWidth;
      const maxScrollLeft = scrollWidth - clientWidth;

      container.classList.remove(
        "scroll-left",
        "scroll-middle",
        "scroll-right",
      );

      if (scrollWidth <= clientWidth + 1) return; // No scroll needed

      if (scrollLeft <= 0) {
        container.classList.add("scroll-right"); // Only right blur
      } else if (scrollLeft >= maxScrollLeft - 1) {
        container.classList.add("scroll-left"); // Only left blur
      } else {
        container.classList.add("scroll-middle"); // Both blurs
      }
    };

    handleScroll(); // initialize

    tableBody.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      tableBody.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [dataSource, pagination.current]);

  return (
    <div className="reusable-table-container" ref={tableRef}>
      <Space
        className="reusable-table"
        direction="vertical"
        style={{ width: "100%" }}
      >
        <div className="table-main-component">
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey={rowKey}
            loading={loading}
            pagination={false}
            bordered={bordered}
            className={`custom-table ${pointer ? "pointer" : ""} ${className}`}
            scroll={{ x: "max-content" }}
            expandable={expandable}
            rowSelection={rowSelection || undefined}
            rowClassName={rowClass}
            /* A column declaring `sorter: true` asks antd for server-side
               sorting, which only reports through onChange - without one the
               header arrows rendered and toggled but nothing ever ran. */
            onChange={
              onSortChange
                ? (_pagination, _filters, sorter) => {
                    const active = Array.isArray(sorter) ? sorter[0] : sorter;
                    const field = active?.field ?? active?.columnKey;
                    onSortChange(
                      active?.order
                        ? {
                            field: Array.isArray(field)
                              ? field.join(".")
                              : field,
                            order: active.order,
                          }
                        : { field: null, order: null },
                    );
                  }
                : undefined
            }
            onRow={(record) => ({
              onClick: (event) => {
                /* the selection checkbox lives inside the row, so a tick used
                   to count as a row click and navigate away with it */
                if (event.target.closest?.(".ant-table-selection-column")) {
                  return;
                }
                onRowClick?.(record);
              },
            })}
          />
          <div className="table-blur-left" />
          <div className="table-blur-right" />
        </div>

        <div className="customPagination">
          <div className="customPaginationWrapper">
            <div className="pagination_total">
              Showing{" "}
              {Math.min(
                (pagination.current - 1) * pagination.pageSize + 1,
                pagination.total,
              )}{" "}
              -{" "}
              {Math.min(
                pagination.current * pagination.pageSize,
                pagination.total,
              )}{" "}
              of {pagination.total} items
            </div>

            <div className="paginationPages">
              <Button
                onClick={() => {
                  setPagination({ ...pagination, current: 1 });
                  triggerChange?.(1, pagination.pageSize);
                }}
                disabled={pagination.current === 1}
              >
                <NavToFirst />
              </Button>

              <ConfigProvider>
                <Pagination
                  current={pagination.current}
                  showPrevNextJumpers
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger={false}
                  onChange={(page) => {
                    setPagination({ ...pagination, current: page });
                    triggerChange?.(page, pagination.pageSize);
                  }}
                  itemRender={(page, type, originalElement) => {
                    if (type === "prev") {
                      return React.cloneElement(
                        originalElement,
                        {},
                        <PrevIcon />,
                      );
                    }
                    if (type === "next") {
                      return React.cloneElement(
                        originalElement,
                        {},
                        <NextIcon />,
                      );
                    }
                    return originalElement;
                  }}
                />
              </ConfigProvider>

              <Button
                onClick={() => {
                  setPagination({ ...pagination, current: totalPages });
                  triggerChange?.(totalPages, pagination.pageSize);
                }}
                disabled={pagination.current === totalPages}
              >
                <NavToLast />
              </Button>
            </div>

            <div className="paginationSize">
              <Select
                value={pagination.pageSize}
                onChange={(newSize) => {
                  setPagination({
                    current: 1,
                    pageSize: newSize,
                    total: pagination.total,
                  });
                  setPageSize?.(newSize);
                  triggerChange?.(1, newSize);
                }}
              >
                {[5, 10, 20, 50, 100].map((size) => (
                  <Option key={size} value={size}>
                    {size}
                  </Option>
                ))}
              </Select>
              Items per Page
            </div>
          </div>
        </div>
      </Space>
    </div>
  );
};

export default memo(ReusableTable);
